import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { IAppState } from "src/redux";
import styled, { createGlobalStyle } from "styled-components";
import { redirectBrowserToURL } from "src/routes";
import { getAppEnvironment } from "src/util/init";
import { translateString } from "src/util/localization";
import NotoSans from "./mw-fonts/NotoSansJP-Regular.woff";
import WarningIcon from "./mw-icons/active-acknowledged-warning.svg";
import ChevronRightIcon from "./mw-icons/chevron-right.svg";

const MyWartsilaFontFace = createGlobalStyle`
  @font-face {
    font-family: "Noto Sans";
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url(${NotoSans}) format("woff");
  }
`;

const BannerContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  padding: 16px;
  background: #ffe7b7;
  border-radius: 2px;
  color: #00030a;
  font-family: "Noto Sans", "Roboto", Arial, Helvetica, sans-serif;
  font-style: normal;
  font-weight: 400;
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 18px;
  letter-spacing: 0;

  > img {
    width: 24px;
    height: 24px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  line-height: 120%;
`;

const Text = styled.div`
  flex: 1;
`;

const MyWartsilaButton = styled.button`
  display: flex;
  padding: 8px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 4px;
  border: none;
  background: #004666;
  color: #fff;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  letter-spacing: 0;
  cursor: pointer;

  > img {
    width: 16px;
    height: 16px;
  }
`;

export interface IMyWartsilaBannerStateProps {
  isMyWartsilaUser: boolean;
  hasLoggedInToMyWartsila: boolean;
}

export const MyWartsilaBanner = () => {
  const { isMyWartsilaUser, hasLoggedInToMyWartsila } = useSelector<IAppState, IMyWartsilaBannerStateProps>(
    (state) => ({
      isMyWartsilaUser: Boolean(state.userInfo.isMyWartsilaUser),
      hasLoggedInToMyWartsila: Boolean(state.userInfo.hasLoggedInToMyWartsila),
    })
  );

  const myWartsilaUri = useMemo(() => {
    const env = getAppEnvironment();
    switch (env) {
      case "local":
      case "dev":
        return "https://my-dev.wartsila.com";
      case "qa":
      case "test":
        return "https://my-qa.wartsila.com";
      case "prod":
      default:
        return "https://my.wartsila.com";
    }
  }, []);

  if (!isMyWartsilaUser) return null;

  return (
    <>
      <MyWartsilaFontFace />
      <BannerContainer>
        <TitleWrapper>
          <IconWrapper>
            <img src={WarningIcon} alt="Warning" />
          </IconWrapper>
          {!hasLoggedInToMyWartsila && <Text>{translateString("myWartsila.welcomeToMyWartsila")}</Text>}
          {hasLoggedInToMyWartsila && <Text>{translateString("myWartsila.myWartsilaIsReady")}</Text>}
        </TitleWrapper>
        <ContentWrapper>
          <IconWrapper />
          {!hasLoggedInToMyWartsila && <Text>{translateString("myWartsila.youHaveBeenSelected")}</Text>}
          {hasLoggedInToMyWartsila && (
            <>
              <Text>{translateString("myWartsila.accessAlreadyActivated")}</Text>
              <MyWartsilaButton onClick={() => redirectBrowserToURL(myWartsilaUri)}>
                <span>{translateString("myWartsila.continueInMyWartsila")}</span>
                <img src={ChevronRightIcon} alt="Chevron right" />
              </MyWartsilaButton>
            </>
          )}
        </ContentWrapper>
      </BannerContainer>
    </>
  );
};
